import { ScaleAgentConfig } from '../types/scaleAgent';

/**
 * Configuration par défaut du Scale Agent
 */
export const DEFAULT_SCALE_AGENT_CONFIG: ScaleAgentConfig = {
  wsUrl: 'ws://127.0.0.1:9091/weigh/stream',
  port: 9091,
  pontBasculeName: 'Pont Bascule Principal',
  siteId: '',
  pontBasculeId: '',
  reconnect: {
    enabled: true,
    maxAttempts: 5,
    delay: 1000,
  },
  scale: {
    unit: 'kg',
    precision: 2,
    stabilityThreshold: 0.01,
  },
};

/**
 * Configurations par environnement
 */
export const SCALE_AGENT_CONFIGS = {
  development: {
    ...DEFAULT_SCALE_AGENT_CONFIG,
    wsUrl: 'ws://127.0.0.1:9091/weigh/stream',
    reconnect: {
      enabled: true,
      maxAttempts: 3,
      delay: 500,
    },
  },
  production: {
    ...DEFAULT_SCALE_AGENT_CONFIG,
    reconnect: {
      enabled: true,
      maxAttempts: 10,
      delay: 2000,
    },
  },
  test: {
    ...DEFAULT_SCALE_AGENT_CONFIG,
    wsUrl: 'ws://127.0.0.1:9091/weigh/stream',
    reconnect: {
      enabled: false,
      maxAttempts: 0,
      delay: 1000,
    },
  },
};

/**
 * Obtenir la configuration selon l'environnement
 */
export function getScaleAgentConfig(): ScaleAgentConfig {
  const env = (import.meta.env as { MODE?: string }).MODE === 'development' ? 'development' : 'production';
  return SCALE_AGENT_CONFIGS[env as keyof typeof SCALE_AGENT_CONFIGS] || DEFAULT_SCALE_AGENT_CONFIG;
}

/**
 * Valider une configuration
 */
export function validateScaleAgentConfig(config: ScaleAgentConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!config.wsUrl) {
    errors.push('URL WebSocket requise');
  }

  if (!config.port || config.port < 1 || config.port > 65535) {
    errors.push('Port invalide (1-65535)');
  }

  if (!config.pontBasculeName) {
    errors.push('Nom du pont bascule requis');
  }

  if (!config.siteId) {
    errors.push('ID du site requis');
  }

  if (!config.pontBasculeId) {
    errors.push('ID du pont bascule requis');
  }

  if (config.reconnect.maxAttempts < 0) {
    errors.push('Nombre de tentatives de reconnexion invalide');
  }

  if (config.reconnect.delay < 100) {
    errors.push('Délai de reconnexion trop court (minimum 100ms)');
  }

  if (config.scale.precision < 0 || config.scale.precision > 6) {
    errors.push('Précision invalide (0-6 décimales)');
  }

  if (config.scale.stabilityThreshold <= 0) {
    errors.push('Seuil de stabilité invalide');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Générer une configuration pour un pont bascule
 */
export function generatePontBasculeConfig(
  pontBasculeName: string,
  siteId: string,
  pontBasculeId: string,
  port: number = 9090
): ScaleAgentConfig {
  return {
    ...DEFAULT_SCALE_AGENT_CONFIG,
    wsUrl: `ws://127.0.0.1:${port}/weight`,
    port,
    pontBasculeName,
    siteId,
    pontBasculeId,
  };
}
